import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"

import { feedTypes, groupFeedSortTypes } from "@/constants/postsFeed.js"

import * as groupApi from "@/api/groupApi.js"
import * as hashtagApi from "@/api/hashtagApi.js"
import * as userApi from "@/api/userApi.js"
import * as accountsApi from "@/api/accountsApi.js"
import * as eventsApi from "@/api/eventsApi.js"
import * as bookmarksApi from "@/api/bookmarksApi.js"

import * as urls from "@/services/urls.js"
import * as postablesService from "@/services/postablesService.js"
import * as sentryReporter from "@/services/sentryReporter.js"

import { shareInAppTypes } from "@/constants/shareInApp.js"
import { postableTypes } from "@/constants/postables.js"

import routes from "@/routes.js"

export const getFetchStrategy = (feedType, groupFeedSortType) => {
  switch (feedType) {
    case feedTypes.HASHTAG:
      return { apiMethod: hashtagApi.posts, transformResponse: setPostData, dataKey: "posts" }
    case feedTypes.PROFILE:
      return { apiMethod: userApi.posts, transformResponse: setPostData, dataKey: "posts" }
    case feedTypes.ACTIVITY_FEED:
      return { apiMethod: groupApi.activityFeedPosts, transformResponse: transformActivityFeedPostsResponse, dataKey: "posts" }
    case feedTypes.GROUP:
      return {
        apiMethod: groupFeedSortType === groupFeedSortTypes.LATEST ? groupApi.recentPosts : groupApi.hottestPosts,
        transformResponse: setPostData,
        dataKey: "posts"
      }
    default:
      throw Error("Feed type not supported")
  }
}

const transformActivityFeedPostsResponse = (response) => {
  const groupStore = useGroupsStore()
  response.posts = response.posts.filter((post) => groupStore.userGroups.find((group) => group.key === post.group.key))

  return setPostData(response)
}

export const setPostData = async (response) => {
  const userSessionStore = useUserSessionStore()
  if (response.posts?.length && userSessionStore.isLoggedIn) {
    await Promise.all([setBadgesBookmarksAndLikes(response.posts), setSharedDataAndPolls(response.posts)])
  }

  return response
}

export const setBadgesBookmarksAndLikes = async (posts) => {
  const userSessionStore = useUserSessionStore()

  if (posts?.length && userSessionStore.isLoggedIn) {
    await Promise.all([
      postablesService.setPostablesAuthorBadges(posts, postableTypes.POST),
      postablesService.setPostablesLikes(posts, postableTypes.POST),
      setPostBookmarks(posts)
    ])
  }
}

export const setSharedDataAndPolls = async (posts) => {
  const userSessionStore = useUserSessionStore()

  if (posts?.length && userSessionStore.isLoggedIn) {
    await Promise.all([setPollVotesOnPostsWhereCurrentUserVoted(posts), setSharedDataOnPosts(posts)])
  }
}

export const setPostBookmarks = async (posts) => {
  const userSessionStore = useUserSessionStore()

  if (!userSessionStore.isLoggedIn || !posts.length) {
    return
  }

  try {
    const { bookmarks } = await bookmarksApi.bookmarkedPosts(posts.map((p) => p.id))
    const bookmarkedIds = bookmarks.map((b) => b.bookmarkable_id)
    posts.forEach((p) => {
      if (bookmarkedIds.includes(p.id)) {
        const bookmark = bookmarks.find((b) => b.bookmarkable_id === p.id)
        p.bookmarkData = bookmark
        p.saved = true
      } else {
        p.saved = false
      }
    })
  } catch (err) {
    sentryReporter.reportError(err)
  }
}

const setPollVotesOnPostsWhereCurrentUserVoted = async (posts) => {
  const pollPosts = posts.filter((p) => p.poll)

  if (!pollPosts.length) {
    return
  }

  const questionsIds = Array.prototype.concat.apply(
    [],
    pollPosts.map((p) => p.poll.questions.map((q) => q.id))
  )
  try {
    const { votes } = await accountsApi.pollVotes(questionsIds)

    votes.forEach((v) => {
      const postForVote = pollPosts.find((p) => p.poll.questions.map((q) => q.id).includes(v.question_id))
      postForVote.poll.chosenQuestionId = v.question_id
    })
  } catch (err) {
    sentryReporter.reportError(err)
  }
}

export const setSharedDataOnPosts = async (posts) => {
  posts.forEach((post) => {
    const route = urls.getRouterRouteFromDeepLink(post.action_button_url)
    if (!route) {
      return
    }
    if (route.name === routes.GROUPS && route.params?.groupId) {
      const groupsStore = useGroupsStore()
      const group = groupsStore.groupForKey(route.params.groupId)
      if (group) {
        post.sharedData = {
          type: shareInAppTypes.GROUP,
          item: group,
          data: {
            members: []
          },
          loaded: false
        }
      }
    } else if (route.name === routes.EVENT && route.params?.id) {
      post.sharedData = {
        type: shareInAppTypes.EVENT,
        item: { id: Number(route.params.id) },
        data: {},
        loaded: false
      }
    }
  })

  await Promise.all([
    setSharedGroupsDataOnPosts(posts.filter((p) => p.sharedData?.type === shareInAppTypes.GROUP)),
    setSharedEventsDataOnPosts(posts.filter((p) => p.sharedData?.type === shareInAppTypes.EVENT))
  ])
}

const setSharedEventsDataOnPosts = async (posts) => {
  if (!posts.length) {
    return
  }

  try {
    const eventIds = posts.map((p) => p.sharedData.item.id)
    const uniqueEventIds = [...new Set(eventIds)]

    //Backend wanted to keep existing endpoints for now
    //If there will be problem with too many requests then we can add new endpoint
    const response = await Promise.allSettled(uniqueEventIds.map((id) => eventsApi.get(id)))
    const eventsData = response.filter((r) => r.status === "fulfilled").map((r) => r.value)

    const rsvpAbleEventIds = eventsData.filter((d) => d.event.rsvpable).map((d) => d.event.id)

    let rsvpValues = []
    let rsvpMeta = []

    if (rsvpAbleEventIds.length) {
      ;[{ event_rsvps: rsvpValues }, { event_rsvps: rsvpMeta }] = await eventsApi.rsvpData(rsvpAbleEventIds)
    }

    posts.forEach((p) => {
      const relatedEvent = eventsData.find((d) => d.event.id === p.sharedData.item.id)?.event
      if (!relatedEvent) {
        p.sharedData = null
        return
      }
      p.sharedData.item = relatedEvent
      if (relatedEvent.rsvpable) {
        p.sharedData.data.rsvpMeta = rsvpMeta.find((m) => m.event_id === relatedEvent.id)
        p.sharedData.data.rsvpValue = rsvpValues.find((v) => v.event_id === relatedEvent.id)?.value
      }
      p.sharedData.loaded = true
    })
  } catch (err) {
    sentryReporter.reportError(err)
  }
}

const setSharedGroupsDataOnPosts = async (posts) => {
  if (!posts.length) {
    return
  }

  try {
    const groupKeys = posts.filter((p) => p.sharedData.item.members_viewable).map((p) => p.sharedData.item.key)
    const uniqueGroupKeys = [...new Set(groupKeys)]

    const { group_members_collage: groupsMembers } = await groupApi.collageMembers({ groupKeys: uniqueGroupKeys })

    posts.forEach((p) => {
      p.sharedData.data.members = groupsMembers[p.sharedData.item.key] || []
      p.sharedData.loaded = true
    })
  } catch (err) {
    sentryReporter.reportError(err)
  }
}

export const updateSharedEventDataForPosts = async ({ posts, eventId }) => {
  const postsToUpdate = posts.filter((post) => {
    const route = urls.getRouterRouteFromDeepLink(post.action_button_url)
    return post.sharedData && route?.name === routes.EVENT && Number(route?.params?.id) === eventId
  })

  await setSharedEventsDataOnPosts(postsToUpdate)
}

export const filterOutAlreadyLoadedPostsFromResponse = ({ response, existingPosts }) => {
  return {
    ...response,
    posts: response.posts.filter((post) => !existingPosts.find((existingPost) => post.id === existingPost.id))
  }
}

export const getProcessedVideos = (post) => {
  return post.videos ? post.videos.filter((v) => Object.keys(v.versions).length) : []
}

export const getProcessedImages = (post) => {
  const attachedImages = post.images ? post.images.filter((i) => Object.keys(i.versions).length) : []
  const attachedGifs = post.gifs
    ? post.gifs
        .filter((g) => Object.keys(g.mp4?.versions || {}).length || Object.keys(g.gif?.versions || {}).length)
        .map((g) => ({ isMp4: Boolean(g.mp4), isGif: Boolean(g.gif), ...(g.mp4 || g.gif) }))
    : []
  return [...attachedImages, ...attachedGifs]
}
